import React, { useMemo } from 'react';
import * as PropTypes from 'prop-types';

const Input = React.forwardRef(
    (
        {
            name,
            label,
            texteAide,
            disabled,
            type,
            required,
            icone,
            longueurMax,
            boutonAction,
            uppercase,
            maskFonction,
            onChange,
            onBlur,
            error,
            warning,
            valid,
            ...otherProps
        },
        ref,
    ) => {
        let ChampsTag = `input`;

        if (type === 'textarea') {
            ChampsTag = type;
        }

        let texteValidation;
        let inputGroupClassName = 'fr-input-group';
        let inputClassName = 'fr-input';

        if (disabled) {
            inputGroupClassName += ' fr-input-group--disabled';
            inputClassName += ' fr-input--disabled';
        } else if (error) {
            inputGroupClassName += ' fr-input-group--error';
            inputClassName += ' fr-input--error';
            if (typeof error === 'object' && 'message' in error && error.message) {
                texteValidation = {
                    id: `${name}-desc-error`,
                    message: error.message,
                    className: 'fr-error-text',
                };
            }
        } else if (warning) {
            inputGroupClassName += ' fr-input-group--warning';
            inputClassName += ' fr-input--warning';
            if (typeof warning === 'object' && 'message' in warning && warning.message) {
                texteValidation = {
                    id: `${name}-desc-warning`,
                    message: warning.message,
                    className: 'fr-warning-text',
                };
            }
        } else if (valid) {
            inputGroupClassName += ' fr-input-group--valid';
            inputClassName += ' fr-input--valid';
            if (typeof valid === 'object' && 'message' in valid && valid.message) {
                texteValidation = {
                    id: `${name}-desc-valid`,
                    message: valid.message,
                    className: 'fr-valid-text',
                };
            }
        }

        const onChangeSurcharge = (event) => {
            const { value } = event.target;
            let { selectionStart } = event.target;
            if (!selectionStart) {
                selectionStart = 0;
            }
            if (uppercase) {
                // eslint-disable-next-line no-param-reassign
                event.target.value = `${value}`.toUpperCase();
            }

            if (maskFonction) {
                const valueMasked = maskFonction(value);
                selectionStart = selectionStart + valueMasked.length - value.length;
                // eslint-disable-next-line no-param-reassign
                event.target.value = valueMasked;
            }

            if (type !== 'number' && type !== 'date') {
                event.target.setSelectionRange(selectionStart, selectionStart);
            }

            if (onChange) {
                onChange(event);
            }
        };

        let styleGroup = {};

        if (longueurMax) {
            styleGroup = { width: '90%', maxWidth: longueurMax };
        }

        const renduChamp = useMemo(() => {
            let champsTag = (
                <ChampsTag
                    ref={ref}
                    id={name}
                    name={name}
                    type={type}
                    onChange={onChangeSurcharge}
                    onBlur={onBlur}
                    className={inputClassName}
                    disabled={disabled}
                    required={required}
                    {...otherProps}
                    aria-describedby={texteValidation && texteValidation.id}
                />
            );
            if (type === 'textarea') {
                return champsTag;
            }
            if (icone) {
                champsTag = <div className={`fr-input-wrap ${icone}`}>{champsTag}</div>;
            }
            if (boutonAction) {
                return (
                    <div>
                        {boutonAction}
                        {champsTag}
                    </div>
                );
            }
            return champsTag;
        }, [
            ref,
            name,
            type,
            onChangeSurcharge,
            onBlur,
            inputClassName,
            disabled,
            required,
            otherProps,
            texteValidation,
        ]);

        return (
            <div className={inputGroupClassName} style={styleGroup}>
                <div className="fr-label">
                    <div className="fr-label-texte">
                        <label htmlFor={name} style={{ float: 'left' }}>
                            {label}
                        </label>
                        {renduChamp}
                    </div>
                    {texteAide && <span className="fr-hint-text">{texteAide}</span>}
                </div>

                {texteValidation && (
                    <p id={texteValidation.id} className={texteValidation.className}>
                        {texteValidation.message}
                    </p>
                )}
            </div>
        );
    },
);

Input.propTypes = {
    name: PropTypes.string,
    label: PropTypes.string,
    texteAide: PropTypes.string,
    disabled: PropTypes.bool,
    type: PropTypes.string,
    required: PropTypes.bool,
    icone: PropTypes.any,
    longueurMax: PropTypes.number,
    boutonAction: PropTypes.func,
    uppercase: false,
    maskFonction: PropTypes.func,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    error: PropTypes.oneOfType([
        PropTypes.shape({
            message: PropTypes.string,
        }),
        PropTypes.bool,
    ]),
    warning: PropTypes.oneOfType([
        PropTypes.shape({
            message: PropTypes.string,
        }),
        PropTypes.bool,
    ]),
    valid: PropTypes.oneOfType([
        PropTypes.shape({
            message: PropTypes.string,
        }),
        PropTypes.bool,
    ]),
};

Input.defaultProps = {
    name: null,
    label: null,
    texteAide: null,
    disabled: false,
    type: null,
    required: false,
    icone: null,
    longueurMax: null,
    boutonAction: null,
    uppercase: false,
    maskFonction: null,
    onChange: null,
    onBlur: null,
    error: null,
    warning: null,
    valid: null,
};

export default Input;
